export interface DragDropTouchOptions {
  allowDragScroll?: boolean;
  contextMenuDelayMS?: number;
  dragImageOpacity?: number;
  dragScrollPercentage?: number;
  dragScrollSpeed?: number;
  dragThresholdPixels?: number;
  forceListen?: boolean;
  isPressHoldMode?: boolean;
  pressHoldDelayMS?: number;
  pressHoldMargin?: number;
  pressHoldThresholdPixels?: number;
}

class DragDropTouchUtil {
  private static readonly _kbdProps = ['altKey', 'ctrlKey', 'metaKey', 'shiftKey'];
  private static readonly _ptProps = [
    'pageX',
    'pageY',
    'clientX',
    'clientY',
    'screenX',
    'screenY',
    'offsetX',
    'offsetY',
  ];

  public static pointFrom(e: TouchEvent, page = false): { x: number; y: number } {
    const t = e.touches[0];
    return {
      x: page ? t.pageX : t.clientX,
      y: page ? t.pageY : t.clientY,
    };
  }

  public static newForwardableEvent(
    type: string,
    srcEvent: TouchEvent,
    target: EventTarget & Element,
  ): Event & {
    dataTransfer?: DataTransfer;
    button?: number;
    which?: number;
    buttons?: number;
  } {
    const evt = new Event(type, {
      bubbles: true,
      cancelable: true,
    }) as any;

    const touch = srcEvent.touches[0];
    evt.button = 0;
    evt.which = evt.buttons = 1;

    this.copyProps(evt, srcEvent, this._kbdProps);
    this.copyProps(evt, touch, this._ptProps);
    this.setOffsetAndLayerProps(evt, target);

    return evt;
  }

  public static setOffsetAndLayerProps(e: any, target: Element): void {
    const rect = target.getBoundingClientRect();
    if (e.offsetX == null) {
      e.offsetX = e.clientX - rect.x;
      e.offsetY = e.clientY - rect.y;
    }
    if (e.layerX == null) {
      e.layerX = e.pageX - rect.left;
      e.layerY = e.pageY - rect.top;
    }
  }

  public static cloneNodeDeep(el: HTMLElement): HTMLElement {
    const clone = el.cloneNode(true) as HTMLElement;
    this.copyStyle(el, clone);
    return clone;
  }

  public static copyStyle(src: Element, dst: Element): void {
    this.removeTroublesomeAttributes(dst);

    if (src instanceof HTMLCanvasElement && dst instanceof HTMLCanvasElement) {
      dst.width = src.width;
      dst.height = src.height;
      dst.getContext('2d')?.drawImage(src, 0, 0);
    }

    this.copyComputedStyles(src, dst);
    (dst as HTMLElement).style.pointerEvents = 'none';

    for (let i = 0; i < src.children.length; i++) {
      this.copyStyle(src.children[i], dst.children[i]);
    }
  }

  public static copyComputedStyles(src: Element, dst: Element): void {
    const computed = getComputedStyle(src);
    for (const key of computed) {
      if (key.includes('transition')) continue;
      (dst as HTMLElement).style.setProperty(key, computed.getPropertyValue(key));
    }

    for (const key of Object.keys((dst as HTMLElement).dataset)) {
      delete (dst as HTMLElement).dataset[key];
    }
  }

  public static removeTroublesomeAttributes(el: Element): void {
    ['id', 'class', 'style', 'draggable'].forEach((attr) => {
      el.removeAttribute(attr);
    });
  }

  private static copyProps(dst: any, src: any, props: string[]): void {
    for (const p of props) {
      dst[p] = src[p];
    }
  }

  public static getDirectParentMatching(el: HTMLElement | null, selector: string): HTMLElement | null {
    return el?.parentElement?.matches(selector) ? el.parentElement : null;
  }

  public static findDraggable(el: HTMLElement | null): HTMLElement | null {
    let currentElement = el;

    while (currentElement && currentElement !== document.body) {
      if (currentElement.hasAttribute('draggable') || currentElement.getAttribute('data-drag-handle') === 'true') {
        return currentElement;
      }
      currentElement = currentElement.parentElement;
    }
    return null;
  }

  public static closestMatching(el: Element | null, selector: string): HTMLElement | null {
    return el?.closest(selector) as HTMLElement | null;
  }

  public static isDragHandle(el: Element): boolean {
    return el.getAttribute('data-drag-handle') === 'true';
  }

  public static getDropTarget(el: Element): HTMLElement | null {
    const selector = '[data-drop-target], [droppable="true"]';
    return this.closestMatching(el, selector);
  }

  public static createGhostElement(src: HTMLElement): HTMLElement {
    const ghost = this.cloneNodeDeep(src);
    ghost.style.position = 'fixed';
    ghost.style.pointerEvents = 'none';
    ghost.style.top = '0';
    ghost.style.left = '0';
    ghost.style.zIndex = '9999';
    ghost.style.opacity = '0.7';
    return ghost;
  }

  public static findScrollableParent(el: HTMLElement): HTMLElement | null {
    let currentElement: HTMLElement | null = el;
    while (currentElement && currentElement !== document.body) {
      if (currentElement.scrollHeight > currentElement.clientHeight) {
        return currentElement;
      }
      currentElement = currentElement.parentElement;
    }
    return null;
  }
}

class DragDTO {
  private _dropEffect: DataTransfer['dropEffect'] = 'move';
  private _effectAllowed: DataTransfer['effectAllowed'] = 'all';
  private _data: Record<string, any> = {};
  private _dragDropTouch: DragDropTouch;

  constructor(dragDropTouch: DragDropTouch) {
    this._dragDropTouch = dragDropTouch;
  }

  public get dropEffect(): DataTransfer['dropEffect'] {
    return this._dropEffect;
  }

  public set dropEffect(value: DataTransfer['dropEffect']) {
    this._dropEffect = value;
  }

  public get effectAllowed(): DataTransfer['effectAllowed'] {
    return this._effectAllowed;
  }

  public set effectAllowed(value: DataTransfer['effectAllowed']) {
    this._effectAllowed = value;
  }

  public get types(): string[] {
    return Object.keys(this._data);
  }

  public clearData(type?: string): void {
    if (type != null) {
      delete this._data[type.toLowerCase()];
    } else {
      this._data = {};
    }
  }

  public getData(type: string): string {
    const lcType = type.toLowerCase();
    let data = this._data[lcType];
    if (lcType === 'text' && data == null) {
      data = this._data['text/plain'];
    }
    return data;
  }

  public setData(type: string, value: string): void {
    this._data[type.toLowerCase()] = value;
  }

  public setDragImage(img: HTMLElement, offsetX: number, offsetY: number): void {
    this._dragDropTouch.setDragImage(img, offsetX, offsetY);
  }
}

const DefaultConfiguration: Required<DragDropTouchOptions> = {
  allowDragScroll: false,
  contextMenuDelayMS: 900,
  dragImageOpacity: 0.5,
  dragScrollPercentage: 10,
  dragScrollSpeed: 10,
  dragThresholdPixels: 5,
  forceListen: false,
  isPressHoldMode: true,
  pressHoldDelayMS: 400,
  pressHoldMargin: 25,
  pressHoldThresholdPixels: 0,
};

export class DragDropTouch {
  public configuration: Required<DragDropTouchOptions>;
  private _dragRoot: Document;
  private _dropRoot: Document;
  private _dragSource: HTMLElement | null = null;
  private _scrollableParent: HTMLElement | null = null;
  private _lastTouch: TouchEvent | null = null;
  private _lastTarget: Element | null = null;
  private _ptDown: { x: number; y: number } | null = null;
  private _isDragEnabled = false;
  private _isDropZone = false;
  private _dataTransfer: DragDTO;
  private _img: HTMLElement | null = null;
  private _imgCustom: HTMLElement | null = null;
  private _imgOffset = { x: 0, y: 0 };
  private _pressHoldIntervalId: number | null = null;

  constructor(dragRoot: Document = document, dropRoot: Document = document, options?: DragDropTouchOptions) {
    this.configuration = { ...DefaultConfiguration, ...options };
    this._dragRoot = dragRoot;
    this._dropRoot = dropRoot;
    this._dataTransfer = new DragDTO(this);
    this.listen();
  }

  private listen(): void {
    if (navigator.maxTouchPoints === 0 && !this.configuration.forceListen) {
      return;
    }

    const opt = { passive: false, capture: false };
    this._dragRoot.addEventListener('touchstart', this._touchstart.bind(this), opt);
    this._dragRoot.addEventListener('touchmove', this._touchmove.bind(this), opt);
    this._dragRoot.addEventListener('touchend', this._touchend.bind(this));
    this._dragRoot.addEventListener('touchcancel', this._touchend.bind(this));
  }

  public setDragImage(img: HTMLElement, offsetX: number, offsetY: number): void {
    this._imgCustom = img;
    this._imgOffset = { x: offsetX, y: offsetY };
  }

  private _touchstart(e: TouchEvent): void {
    if (!this._shouldHandle(e)) return;

    this._reset();
    const src = DragDropTouchUtil.findDraggable(e.target as HTMLElement);
    if (!src) return;

    if (
      e.target &&
      !this._dispatchEvent(e, 'mousemove', e.target as Element) &&
      !this._dispatchEvent(e, 'mousedown', e.target as Element)
    ) {
      this._dragSource = src;
      this._ptDown = DragDropTouchUtil.pointFrom(e);
      this._lastTouch = e;

      setTimeout(() => {
        if (this._dragSource === src && this._img === null) {
          if (this._dispatchEvent(e, 'contextmenu', src)) {
            this._reset();
          }
        }
      }, this.configuration.contextMenuDelayMS);

      if (this.configuration.isPressHoldMode) {
        this._pressHoldIntervalId = window.setTimeout(() => {
          this._isDragEnabled = true;
          this._touchmove(e);
        }, this.configuration.pressHoldDelayMS);
      }
    }
  }

  private _touchmove(e: TouchEvent): void {
    if (this._shouldCancelPressHoldMove(e)) {
      this._reset();
      return;
    }

    if (this._shouldHandleMove(e) || this._shouldHandlePressHoldMove(e)) {
      const target = this._getTarget(e);
      if (this._dispatchEvent(e, 'mousemove', target)) {
        this._lastTouch = e;
        e.preventDefault();
        return;
      }

      if (this._dragSource && !this._img && this._shouldStartDragging(e)) {
        if (this._dispatchEvent(this._lastTouch!, 'dragstart', this._dragSource)) {
          this._dragSource = null;
          return;
        }
        this._createImage(e);
        this._dispatchEvent(e, 'dragenter', target);
      }

      if (this._img && this._dragSource) {
        this._lastTouch = e;
        e.preventDefault();
        this._dispatchEvent(e, 'drag', this._dragSource);

        if (target !== this._lastTarget) {
          if (this._lastTarget) {
            this._dispatchEvent(this._lastTouch, 'dragleave', this._lastTarget);
          }
          this._dispatchEvent(e, 'dragenter', target);
          this._lastTarget = target;
        }

        this._moveImage(e);
        this._isDropZone = this._dispatchEvent(e, 'dragover', target);

        if (this.configuration.allowDragScroll) {
          if (!this._scrollableParent) {
            this._scrollableParent = DragDropTouchUtil.findScrollableParent(target as HTMLElement);
          }

          const delta = this._getHotRegionDelta(e);
          this._scrollableParent?.scrollBy(delta.x, delta.y);
        }
      }
    }
  }

  private _touchend(e: TouchEvent): void {
    if (!(this._lastTouch && e.target && this._lastTarget)) {
      this._reset();
      return;
    }

    if (this._shouldHandle(e)) {
      if (this._dispatchEvent(this._lastTouch, 'mouseup', e.target as Element)) {
        e.preventDefault();
        return;
      }

      if (!this._img) {
        this._dragSource = null;
        this._dispatchEvent(this._lastTouch, 'click', e.target as Element);
      }

      this._destroyImage();

      if (this._dragSource) {
        if (e.type.indexOf('cancel') < 0 && this._isDropZone) {
          this._dispatchEvent(this._lastTouch, 'drop', this._lastTarget);
        }
        this._dispatchEvent(this._lastTouch, 'dragend', this._dragSource);
        this._reset();
      }
    }
  }

  private _shouldHandle(e?: TouchEvent | Event): boolean {
    return !!(e && !e.defaultPrevented && 'touches' in e && e.touches.length < 2);
  }

  private _shouldHandleMove(e: TouchEvent): boolean {
    return !this.configuration.isPressHoldMode && this._shouldHandle(e);
  }

  private _shouldHandlePressHoldMove(e?: TouchEvent | Event): boolean {
    return !!(this.configuration.isPressHoldMode && this._isDragEnabled && e && 'touches' in e && e.touches.length > 0);
  }

  private _shouldCancelPressHoldMove(e: TouchEvent): boolean {
    return (
      this.configuration.isPressHoldMode &&
      !this._isDragEnabled &&
      this._getDelta(e) > this.configuration.pressHoldMargin
    );
  }

  private _shouldStartDragging(e: TouchEvent): boolean {
    const delta = this._getDelta(e);
    if (this.configuration.isPressHoldMode) {
      return delta >= this.configuration.pressHoldThresholdPixels;
    }
    return delta > this.configuration.dragThresholdPixels;
  }

  private _reset(): void {
    this._destroyImage();
    this._dragSource = null;
    this._lastTouch = null;
    this._lastTarget = null;
    this._ptDown = null;
    this._isDragEnabled = false;
    this._isDropZone = false;
    this._dataTransfer = new DragDTO(this);
    if (this._pressHoldIntervalId) {
      clearTimeout(this._pressHoldIntervalId);
      this._pressHoldIntervalId = null;
    }
  }

  private _getDelta(e: TouchEvent): number {
    if (!this._ptDown) return 0;
    const { x, y } = this._ptDown;
    const p = DragDropTouchUtil.pointFrom(e);
    return Math.sqrt((p.x - x) ** 2 + (p.y - y) ** 2);
  }

  private _getHotRegionDelta(e: TouchEvent): { x: number; y: number } {
    const { clientX: x, clientY: y } = e.touches[0];
    const { innerWidth: w, innerHeight: h } = window;
    const { dragScrollPercentage, dragScrollSpeed } = this.configuration;
    const v1 = dragScrollPercentage / 100;
    const v2 = 1 - v1;
    // eslint-disable-next-line no-nested-ternary
    const dx = x < w * v1 ? -dragScrollSpeed : x > w * v2 ? +dragScrollSpeed : 0;
    // eslint-disable-next-line no-nested-ternary
    const dy = y < h * v1 ? -dragScrollSpeed : y > h * v2 ? +dragScrollSpeed : 0;
    return { x: dx, y: dy };
  }

  private _getTarget(e: TouchEvent): Element {
    const pt = DragDropTouchUtil.pointFrom(e);
    let el = this._dropRoot.elementFromPoint(pt.x, pt.y);
    while (el && getComputedStyle(el).pointerEvents === 'none') {
      el = el.parentElement;
    }
    return el || document.body;
  }

  private _createImage(e: TouchEvent): void {
    if (this._img) {
      this._destroyImage();
    }

    const src = this._imgCustom || this._dragSource!;
    this._img = DragDropTouchUtil.createGhostElement(src);

    if (!this._imgCustom) {
      const rc = src.getBoundingClientRect();
      const pt = DragDropTouchUtil.pointFrom(e);
      this._imgOffset = { x: pt.x - rc.left, y: pt.y - rc.top };
      this._img.style.opacity = this.configuration.dragImageOpacity.toString();
    }

    this._moveImage(e);
    document.body.appendChild(this._img);
  }

  private _destroyImage(): void {
    if (this._img && this._img.parentElement) {
      this._img.parentElement.removeChild(this._img);
    }
    this._img = null;
    this._imgCustom = null;
  }

  private _moveImage(e: TouchEvent): void {
    requestAnimationFrame(() => {
      if (this._img) {
        const pt = DragDropTouchUtil.pointFrom(e, true);
        const s = this._img.style;
        s.position = 'fixed';
        s.pointerEvents = 'none';
        s.zIndex = '999999';
        s.left = `${Math.round(pt.x - this._imgOffset.x)}px`;
        s.top = `${Math.round(pt.y - this._imgOffset.y)}px`;
      }
    });
  }

  private _dispatchEvent(srcEvent: TouchEvent, type: string, target: Element): boolean {
    const evt = DragDropTouchUtil.newForwardableEvent(type, srcEvent, target);
    evt.dataTransfer = this._dataTransfer as unknown as DataTransfer;
    target.dispatchEvent(evt);
    return evt.defaultPrevented;
  }
}

/**
 * enables drag and drop on mobile devices
 * this is a modified version of the original code from the library
 * https://github.com/drag-drop-touch-js/dragdroptouch
 * the original has a crucial bug that scrolls the page when dragging
 */
export function enableDragDropTouch(
  dragRoot: Document = document,
  dropRoot: Document = document,
  options?: DragDropTouchOptions,
): void {
  new DragDropTouch(dragRoot, dropRoot, options);
}
